import React, { useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Link,
  TextField,
  Typography,
  CircularProgress,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { App as CapApp } from '@capacitor/app';

// Ícone do Google SVG
const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>
);

// Ícone da Apple SVG (preto para fundo branco)
const AppleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="#000000">
    <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
  </svg>
);

interface LoginFormProps {
  onSignUpClick: () => void;
}

const LoginForm: React.FC<LoginFormProps> = ({ onSignUpClick }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const { signIn, signInWithGoogle, signInWithApple } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!email.trim() || !password.trim()) {
      setError('Preencha todos os campos');
      return;
    }

    setIsLoading(true);
    try {
      await signIn(email, password);
      setSuccess('Login realizado com sucesso');
      setTimeout(() => navigate('/dashboard', { replace: true }), 1000);
    } catch (err: any) {
      const code = err?.code;
      if (code === 'auth/user-not-found') {
        setError('Conta não encontrada. Crie uma conta primeiro!');
      } else if (code === 'auth/invalid-credential') {
        setError('Email ou senha incorretos. Não tem conta? Crie uma!');
      } else if (code === 'auth/too-many-requests') {
        setError('Muitas tentativas. Tente mais tarde');
      } else if (code === 'auth/user-disabled') {
        setError('Usuário desativado');
      } else {
        setError('Usuário ou senha incorreta');
      }
      setPassword('');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError('');
    setSuccess('');
    setIsLoading(true);
    try {
      console.log('[LOGIN] Iniciando Google Sign-In...');

      // No iOS, garantir que o app está em foreground e bridge pronto
      if (Capacitor.isNativePlatform()) {
        const state = await CapApp.getState();
        if (!state.isActive) {
          throw new Error('App não está ativo');
        }
        // Pequeno delay pra garantir que o viewController tá pronto
        await new Promise(resolve => setTimeout(resolve, 300));
      }

      await signInWithGoogle();
      console.log('[LOGIN] Google Sign-In concluído!');
      setSuccess('Login realizado com sucesso');
      setTimeout(() => navigate('/dashboard', { replace: true }), 1000);
    } catch (err: any) {
      console.error('[LOGIN] Google Auth Error:', err);
      if (err?.message?.includes('não encontrado') || err?.message?.includes('not found')) {
        setError('Conta não encontrada. Crie uma conta primeiro!');
      } else {
        setError(`Falha ao autenticar: ${err?.message || 'Erro desconhecido'}`);
      }
    } finally {
      console.log('[LOGIN] Finalizando Google Sign-In, loading=false');
      setIsLoading(false);
    }
  };

  const handleAppleSignIn = async () => {
    setError('');
    setSuccess('');
    setIsLoading(true);
    try {
      console.log('[LOGIN] Iniciando Apple Sign-In...');

      // No iOS, pequeno delay para garantir que a UI está pronta
      if (Capacitor.isNativePlatform()) {
        await new Promise(resolve => setTimeout(resolve, 300));
      }

      await signInWithApple();
      console.log('[LOGIN] Apple Sign-In concluído!');
      setSuccess('Login realizado com sucesso');
      setTimeout(() => navigate('/dashboard', { replace: true }), 1000);
    } catch (err: any) {
      console.error('[LOGIN] Apple Auth Error:', err);
      if (err?.message?.includes('não encontrado') || err?.message?.includes('not found')) {
        setError('Conta não encontrada. Crie uma conta primeiro!');
      } else if (err?.message?.includes('cancelado') || err?.message?.includes('cancelled')) {
        setError('Login cancelado');
      } else {
        setError(`Falha ao autenticar com Apple: ${err?.message || 'Erro desconhecido'}`);
      }
    } finally {
      console.log('[LOGIN] Finalizando Apple Sign-In, loading=false');
      setIsLoading(false);
    }
  };

  return (
    <>
      <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
        <Button
          fullWidth
          variant="outlined"
          onClick={handleGoogleSignIn}
          disabled={isLoading}
          startIcon={<GoogleIcon />}
          sx={{
            borderColor: theme.colors.border,
            color: theme.colors.text.primary,
            textTransform: 'none',
            fontSize: '0.75rem',
            py: 0.75,
            '&:hover': {
              borderColor: theme.colors.primary
            }
          }}
        >
          Google
        </Button>
        <Button
          fullWidth
          variant="outlined"
          onClick={handleAppleSignIn}
          disabled={isLoading}
          startIcon={<AppleIcon />}
          sx={{
            borderColor: theme.colors.border,
            backgroundColor: '#fff',
            color: '#000',
            textTransform: 'none',
            fontSize: '0.75rem',
            py: 0.75,
            '&:hover': {
              borderColor: theme.colors.primary,
              backgroundColor: '#f5f5f5'
            },
            '&:disabled': {
              borderColor: '#e0e0e0',
              backgroundColor: '#f5f5f5',
              color: '#9e9e9e'
            }
          }}
        >
          Apple
        </Button>
      </Box>
      <form onSubmit={handleSubmit} noValidate>
        <TextField
          fullWidth
          placeholder="E-mail"
          variant="outlined"
          size="medium"
          margin="normal"
          value={email}
          onChange={(e) => { setEmail(e.target.value); setError(''); setSuccess(''); }}
          disabled={isLoading}
          type="email"
          autoComplete="email"
          required
          sx={{ bgcolor: theme.colors.background, borderRadius: 999, '& .MuiOutlinedInput-root': { borderRadius: 999, height: 44, '& fieldset': { borderColor: theme.colors.border }, '&:hover fieldset': { borderColor: theme.colors.primary }, '&.Mui-focused fieldset': { borderColor: theme.colors.primary } }, '& .MuiInputBase-input': { color: theme.colors.text.primary, fontSize: '16px' } }}
          slotProps={{
            inputLabel: { shrink: false }
          }}
        />
        <TextField
          fullWidth
          placeholder="Senha"
          type={showPassword ? 'text' : 'password'}
          variant="outlined"
          size="medium"
          margin="normal"
          value={password}
          onChange={(e) => { setPassword(e.target.value); setError(''); setSuccess(''); }}
          disabled={isLoading}
          autoComplete="current-password"
          required
          sx={{ bgcolor: theme.colors.background, borderRadius: 999, '& .MuiOutlinedInput-root': { borderRadius: 999, height: 44, '& fieldset': { borderColor: theme.colors.border }, '&:hover fieldset': { borderColor: theme.colors.primary }, '&.Mui-focused fieldset': { borderColor: theme.colors.primary } }, '& .MuiInputBase-input': { color: theme.colors.text.primary, fontSize: '16px' } }}
          slotProps={{
            input: { endAdornment: (<InputAdornment position="end"><IconButton aria-label="toggle password visibility" onClick={() => setShowPassword(!showPassword)} edge="end" size="small" sx={{ color: theme.colors.text.secondary }}>{showPassword ? <VisibilityOff /> : <Visibility />}</IconButton></InputAdornment>) },
            inputLabel: { shrink: false }
          }} />
        {error && (<Typography variant="body2" sx={{ color: '#d32f2f', fontSize: '0.8rem', mt: 0.5, mb: 1, textAlign: 'left', px: 1 }}>{error}</Typography>)}
        {success && (<Typography variant="body2" sx={{ color: '#2e7d32', fontSize: '0.8rem', mt: 0.5, mb: 1, textAlign: 'center', fontWeight: 500 }}>{success}</Typography>)}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: (error || success) ? 1 : 0.5, mb: 1.5 }}>
          <FormControlLabel control={<Checkbox size="small" sx={{ p: 0.5, color: theme.colors.text.secondary, '&.Mui-checked': { color: theme.colors.primary } }} checked={remember} onChange={(e) => setRemember(e.target.checked)} disabled={isLoading} />} label={<Typography variant="caption" sx={{ color: theme.colors.text.secondary }}>Lembrar-me</Typography>} />
          <Link component="button" onClick={(e: React.MouseEvent) => e.preventDefault()} variant="caption" underline="hover" sx={{ color: theme.colors.text.secondary, '&:hover': { color: theme.colors.primary } }}>Esqueceu a senha?</Link>
        </Box>
        <Button fullWidth type="submit" disabled={isLoading} sx={{ bgcolor: theme.colors.secondary, color: '#FFFFFF', borderRadius: 999, textTransform: 'none', fontWeight: 700, fontSize: '1.05rem', py: 1, '&:hover': { bgcolor: theme.colors.secondary, opacity: 0.9 }, '&:disabled': { bgcolor: theme.colors.secondary, opacity: 0.7, color: '#FFFFFF' } }}>{isLoading ? (<><CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />{success ? 'Redirecionando...' : 'Entrando...'}</>) : 'Entrar'}</Button>
      </form>
      <Typography variant="caption" sx={{ mt: 1.5, display: 'block', color: theme.colors.text.secondary }}>Não tem uma conta? <Link component="button" onClick={(e: React.MouseEvent) => { e.preventDefault(); onSignUpClick(); }} underline="hover" sx={{ color: theme.colors.primary, cursor: 'pointer', '&:hover': { color: theme.colors.secondary } }}>Criar uma conta</Link></Typography>
    </>
  );
};

export default LoginForm;